// Caché local del POS (IndexedDB) para poder operar desde un arranque en frío sin Internet.
// Solo se usa como respaldo: si hay red, siempre manda el servidor.
import { get, set } from "idb-keyval";

const K = (key) => `aipos_cache_${key}`;
const PIN_KEY = "aipos_pin_cache_v1";
const SHIFT_KEY = "aipos_local_shift_v1";
const DEVICE_LOGIN_KEY = "aipos_device_login_cache_v1";

/** Error de red (sin respuesta del servidor) vs error de negocio (400/403/…). */
export const isNetworkError = (e) => !e?.response;

export async function cachePut(key, data) {
  try { await set(K(key), { at: Date.now(), data }); } catch (_) { /* cuota llena */ }
}

export async function cacheGet(key) {
  try { return (await get(K(key))) || null; } catch (_) { return null; }
}

/**
 * Ejecuta el fetcher y guarda el resultado; si falla por red, devuelve la copia local.
 * Retorna siempre { data, fromCache, at }.
 */
export async function cachedGet(key, fetcher) {
  try {
    const r = await fetcher();
    await cachePut(key, r.data);
    return { data: r.data, fromCache: false, at: Date.now() };
  } catch (e) {
    const c = await cacheGet(key);
    if (c && isNetworkError(e)) return { data: c.data, fromCache: true, at: c.at };
    throw e;
  }
}

/**
 * Catálogo grande (decenas de miles de SKU): primero se consulta la huella del catálogo y
 * solo se vuelve a descargar completo si cambió. Si no cambió, se reutiliza la copia del equipo.
 * Retorna { data, fromCache, at, reused }.
 */
export async function cachedCatalog(key, getVersion, getFull) {
  try {
    const v = (await getVersion()).data;
    const c = await cacheGet(key);
    if (c?.data?.version && c.data.version === v.version && Array.isArray(c.data.items)) {
      return { data: c.data.items, fromCache: false, at: c.at, reused: true, version: v.version };
    }
    const items = (await getFull()).data;
    await cachePut(key, { version: v.version, items });
    return { data: items, fromCache: false, at: Date.now(), reused: false, version: v.version };
  } catch (e) {
    const c = await cacheGet(key);
    if (c && isNetworkError(e)) {
      return { data: c.data?.items || [], fromCache: true, at: c.at, reused: true,
               version: c.data?.version || "" };
    }
    throw e;
  }
}

/** SHA-256 hex (igual que el hash de PIN del backend). */
export async function sha256Hex(txt) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(String(txt)));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Guarda el hash del PIN de un cajero que ya se identificó con el servidor (nunca el PIN). */
export async function rememberOperatorPin(pin, operator) {
  try {
    const hash = await sha256Hex(pin);
    const map = (await get(PIN_KEY)) || {};
    map[hash] = { ...operator, cached_at: Date.now() };
    await set(PIN_KEY, map);
  } catch (_) { /* noop */ }
}

/** Identifica un cajero sin red usando los hashes guardados. */
export async function identifyOperatorOffline(pin) {
  try {
    const hash = await sha256Hex(pin);
    const map = (await get(PIN_KEY)) || {};
    return map[hash] || null;
  } catch (_) { return null; }
}

/**
 * Sesión del login por PIN del equipo emparejado (pantalla /pos-pin).
 * Guardamos la respuesta del servidor indexada por el hash del PIN para poder
 * entrar al POS cuando el equipo arranca sin Internet. Nunca se guarda el PIN.
 */
export async function rememberDeviceLogin(pin, payload) {
  try {
    const hash = await sha256Hex(pin);
    const map = (await get(DEVICE_LOGIN_KEY)) || {};
    map[hash] = { ...payload, cached_at: Date.now() };
    await set(DEVICE_LOGIN_KEY, map);
  } catch (_) { /* noop */ }
}

export async function getDeviceLoginOffline(pin) {
  try {
    const hash = await sha256Hex(pin);
    const map = (await get(DEVICE_LOGIN_KEY)) || {};
    return map[hash] || null;
  } catch (_) { return null; }
}

/** El equipo fue desenlazado (o su terminal ya no existe): borra todo rastro del emparejamiento. */
export async function forgetDevice() {
  ["aipos_device_token", "aipos_device_tipo", "aipos_device_slug", "aipos_device_company", "aipos_device_store",
   "aipos_device_name", "pos_terminal_id"].forEach((k) => localStorage.removeItem(k));
  try { await set(DEVICE_LOGIN_KEY, {}); } catch (_) { /* noop */ }
}

/** Turno abierto offline (cuando la caja se abre sin conexión). */
export async function setLocalShift(shift) {
  try { await set(SHIFT_KEY, shift || null); } catch (_) { /* noop */ }
}

export async function getLocalShift() {
  try { return (await get(SHIFT_KEY)) || null; } catch (_) { return null; }
}
